<script>
import { groupBy, isArray } from "lodash";

export default {
	name: "BaseSelect",

	render() {
		// prefixes denote slot prop types
		// p for component props
		// m for component methods
		return this.$scopedSlots.default({
			pOptionsGrouped: this.optionsGrouped,
			pOptions: this.optionsComputed,
			pOptionGroups: this.optionGroups,
			pInitial: this.initial,
			pExpanded: this.expanded,
			pSelectedOption: this.selectedOption,
			mOpen: this.open,
			mClose: this.close,
			mToggle: this.toggle,
			mSelect: this.select,
			mSelectFirst: this.selectFirst,
			mSelectLast: this.selectLast,
			mSelectDown: this.selectDown,
			mSelectUp: this.selectUp,
			mClear: this.clear,
			cOneOption: this.oneOption
		});
	},

	data() {
		return {
			dSelected: 0,
			initial: false,
			expanded: false
		};
	},

	props: {
		id: {
			type: String,
			default: "",
			required: true
		},

		allowEmpty: {
			type: Boolean,
			default: false
		},

		placeholder: {
			type: String,
			default: ""
		},

		value: {
			type: [String, Number],
			default: ""
		},

		selected: {
			type: Number,
			default: -1
		},

		options: {
			type: [Array, Object],
			default: function () {
				return [];
			}
		},

		showOnOneOption: {
			type: Boolean,
			default: true
		}
	},

	computed: {
		// derive a selected option based on the currently
		// selected index, return "empty" option if list is empty
		selectedOption() {
			return this.optionsComputed[this.dSelected]
				? this.optionsComputed[this.dSelected]
				: {
						id: "",
						value: "",
						label: "",
						group: "",
						selected: false,
						disabled: false
				  };
		},

		optionsComputed() {
			let options;
			let index = -1;

			if (!isArray(this.options)) {
				options = Object.keys(this.options).flatMap((group) => {
					const options = this.options[group];

					return options
						.map((option) => {
							return {
								...option,
								selected: false
							};
						})
						.map((option) => {
							index++;

							return {
								id: `${this.id}-option-${index}`,
								disabled: false,
								group,
								...option,
								selected: this.dSelected === index
							};
						});
				});
			} else {
				options = this.options
					.map((option) => {
						return {
							...option,
							selected: false
						};
					})
					.map((option, index) => {
						return {
							id: `${this.id}-option-${index}`,
							disabled: false,
							group: "",
							...option,
							selected: this.dSelected === index
						};
					});
			}

			return options;
		},

		optionGroups() {
			return groupBy(this.optionsComputed, "group");
		},

		oneOption() {
			return this.showOnOneOption || this.options.length > 1 ? true : false;
		}
	},

	methods: {
		updateSelected() {
			if (this.value) {
				this.updateSelectedByValue();
			} else {
				this.updateSelectedByIndex();
			}
		},

		// update selected index by explicit numerical value
		updateSelectedByIndex() {
			const lowerBound = this.allowEmpty ? -1 : 0;

			// ensure new index is within range
			// allow for a negative -1 value which indicates
			// no option is currently selected
			this.dSelected = Math.min(
				Math.max(lowerBound, this.selected),
				Math.max(lowerBound, this.optionsComputed.length - 1)
			);
		},

		// upodate selected index by value which is used to find
		// a numerical index
		updateSelectedByValue() {
			// find index of value in option list
			const currentIndex = this.optionsComputed.findIndex((option) => {
				return option.value === this.value;
			});

			// update selected with found index
			if (currentIndex >= 0) {
				this.dSelected = currentIndex;
			}

			// allow for updating selected index to -1 if
			// allow empty is enabled
			if (currentIndex < 0 && this.allowEmpty) {
				this.dSelected = currentIndex;
			}
		},

		// move to non-initial open state
		open() {
			if (this.oneOption === true) {
				this.expanded = true;
				this.initial = false;
			}
		},

		// move to closed initial state
		close() {
			if (this.oneOption === true) {
				this.expanded = false;
				this.initial = true;

				// fire only after an index change and the list is closed
				this.emitSelectedChange();
				this.emitActionSelectedChange();
			}
		},

		// clear the selection and move to closed initial state
		clear() {
			this.dSelected = this.allowEmpty ? -1 : 0;
			this.expanded = false;
			this.initial = true;

			// fire only after an index change and the list is closed
			this.emitSelectedChange();
			this.emitActionSelectedChange();
		},

		// move to or from open or closed state
		toggle() {
			if (this.oneOption === true) {
				this.expanded = !this.expanded;
			}
		},

		// select by index and move to closed state
		select(id = "") {
			// we can be confident an index will be found
			const index = this.optionsComputed.findIndex((option) => {
				return option.id === id;
			});

			this.dSelected = index;
			this.expanded = false;
			this.initial = false;

			// fire only after an index change and the list is closed
			this.emitSelectedChange();
			this.emitActionSelectedChange();
		},

		// select first option and move into open state
		selectFirst() {
			const index = 0;

			if (!this.isDisabled(index)) {
				this.dSelected = index;
				this.expanded = true;
				this.initial = false;
			}
		},

		// select last option and move into open state
		selectLast() {
			const index = this.optionsComputed.length - 1;

			if (!this.isDisabled(index)) {
				this.dSelected = index;
				this.expanded = true;
				this.initial = false;
			}
		},

		// select "next" option and move into open state
		selectDown() {
			const index = Math.min(
				this.optionsComputed.length - 1,
				this.dSelected + 1
			);

			if (!this.isDisabled(index)) {
				this.dSelected = index;
				this.expanded = true;
				this.initial = false;
			}
		},

		// select "previous" option and move into open state
		selectUp() {
			const index = Math.max(0, this.dSelected - 1);

			if (!this.isDisabled(index)) {
				this.dSelected = index;
				this.expanded = true;
				this.initial = false;
			}
		},

		// track clicks outside select component, close if click is
		// the component element or a child of component element
		onMouseDown(e) {
			if (!(e.target === this.$el || this.$el.contains(e.target))) {
				this.expanded = false;
			}
		},

		// track focus ins outside select component, close if click is
		// the component element or a child of component element
		onFocusIn(e) {
			if (!(e.target === this.$el || this.$el.contains(e.target))) {
				this.initial = false;
				this.expanded = false;
			}
		},

		// returns the list item at index's disabled value
		isDisabled(index) {
			return this.optionsComputed[index].disabled;
		},

		// emit final change events
		emitSelectedChange() {
			this.$emit(
				"selected-change",
				this.selectedOption.value,
				this.selectedOption.label
			);
			this.$emit("selected-change-index", this.dSelected);
		},

		emitActionSelectedChange() {
			this.$emit(
				"action-selected-change",
				this.selectedOption.value,
				this.selectedOption.label,
				this.dSelected
			);
		}
	},

	watch: {
		value: {
			immediate: true,
			handler: function () {
				this.updateSelected();
				this.emitSelectedChange();
			}
		},

		selected: {
			immediate: true,
			handler: function () {
				this.updateSelected();
				this.emitSelectedChange();
			}
		}
	},

	created() {
		document.addEventListener("mousedown", this.onMouseDown);
		document.addEventListener("focusin", this.onFocusIn);
	},

	destroyed() {
		document.removeEventListener("mousedown", this.onMouseDown);
		document.removeEventListener("focusin", this.onFocusIn);
	}
};
</script>
